import { MapPin, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function MatchBadge({ userId, cached }) {
  // Stop propagation so clicking the badge doesn't open the full user sheet.
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`cached-score-${userId}`}
          title="AI compatibility (cached) — tap to peek"
          onClick={(e) => e.stopPropagation()}
          className="inline-flex items-center gap-1 text-[10px] font-mono px-2 py-0.5 rounded-full border border-[#00E5FF]/30 text-[#00E5FF] bg-[#00E5FF]/5 hover:bg-[#00E5FF]/15 hover:border-[#00E5FF] transition-all"
        >
          <Sparkles size={10} /> {cached.score}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        onClick={(e) => e.stopPropagation()}
        data-testid={`match-explainer-${userId}`}
        className="w-80 bg-[#05050A] border-[#00E5FF]/40 text-white p-0 overflow-hidden shadow-[0_0_40px_rgba(0,229,255,0.15)]"
      >
        {/* Mainframe-styled header */}
        <div className="relative px-4 py-2 border-b border-[#00E5FF]/25 bg-gradient-to-r from-[#00E5FF]/10 to-transparent">
          <div className="flex items-center justify-between">
            <div className="text-[9px] tracking-[0.3em] font-mono text-[#00E5FF]">
              // MAINFRAME · COMPAT MATRIX
            </div>
            <div className="text-[9px] font-mono text-white/40">CACHED</div>
          </div>
        </div>
        {/* Score in an ASCII "magic square" frame */}
        <div className="px-4 pt-4">
          <div className="font-mono text-[9px] text-[#00E5FF]/60 leading-none">┌───────┐</div>
          <div className="flex items-baseline gap-2 font-mono text-[#00E5FF]/60 leading-none">
            <span className="text-[9px]">│</span>
            <span className="font-[Unbounded] text-4xl font-bold text-[#00E5FF] leading-none tracking-tighter">
              {String(cached.score).padStart(3, " ")}
            </span>
            <span className="text-[9px]">│</span>
            <span className="text-[10px] font-mono tracking-widest text-white/40 ml-1">/100</span>
          </div>
          <div className="font-mono text-[9px] text-[#00E5FF]/60 leading-none">└───────┘</div>
        </div>
        {/* Body */}
        <div className="px-4 pt-3 pb-4 space-y-3">
          <div>
            <div className="text-[9px] tracking-[0.3em] font-mono text-white/40">// REASON</div>
            <p className="text-sm text-white/85 mt-1 leading-snug">{cached.reason || "—"}</p>
          </div>
          {cached.icebreaker && (
            <div>
              <div className="text-[9px] tracking-[0.3em] font-mono text-[#39FF14]">// OPENER</div>
              <p className="text-sm text-white/85 mt-1 italic leading-snug">&ldquo;{cached.icebreaker}&rdquo;</p>
            </div>
          )}
          <div className="pt-1 text-[9px] font-mono text-white/30">tap card → send signal</div>
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function NearbyList({ nearby, onOpenUser, cachedScores = {} }) {
  if (nearby.length === 0) {
    return (
      <div className="px-4 md:px-8 pb-10">
        <div className="text-xs tracking-[0.3em] font-mono text-white/40 mb-3">// NEARBY SIGNALS</div>
        <div className="p-8 bg-[#0D0E15] border border-white/10 rounded-2xl text-center text-white/40">
          <MapPin className="mx-auto mb-3 text-[#00E5FF]" />
          No one in range yet. Invite friends to broadcast their signal.
        </div>
      </div>
    );
  }
  return (
    <div className="px-4 md:px-8 pb-10">
      <div className="text-xs tracking-[0.3em] font-mono text-white/40 mb-3">// NEARBY SIGNALS</div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {nearby.map((u) => {
          const cached = cachedScores[u.id];
          return (
            <div
              key={u.id}
              data-testid={`nearby-card-${u.id}`}
              className="p-5 bg-[#0D0E15] border border-white/10 rounded-2xl hover:border-[#00E5FF] hover:-translate-y-0.5 transition-all cursor-pointer"
              onClick={() => onOpenUser(u)}
            >
              <div className="flex items-center justify-between mb-2 gap-2">
                <span className="font-[Unbounded] font-semibold truncate">{u.display_name}</span>
                <div className="flex items-center gap-1 shrink-0">
                  {cached && <MatchBadge userId={u.id} cached={cached} />}
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded-full border border-[#39FF14]/30 text-[#39FF14]">{u.distance_km}km</span>
                </div>
              </div>
              <p className="text-sm text-white/50 line-clamp-2 mb-3">{u.bio || "No bio yet."}</p>
              <div className="flex flex-wrap gap-1">
                {(u.interests || []).slice(0, 3).map((t) => (
                  <span key={t} className="text-[10px] font-mono px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/60">{t}</span>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
